import { streetsMateriallyDiffer } from './addressValidation';
import { US_STATE_CODES } from './markets';

// Beacon writes the same key from js/packages/beacon/lib/manual-address-draft.ts.
// The two packages cannot import each other, so the JSON shape is duplicated.
export const MANUAL_ADDRESS_DRAFT_KEY = 'od-manual-address-draft';
export const MANUAL_ADDRESS_DRAFT_TTL_MS = 30 * 60 * 1000;

export type ManualAddressFields = {
  street: string;
  unit: string;
  city: string;
  state: string;
  postalCode: string;
};

export const MANUAL_ADDRESS_FIELD_KEYS = [
  'street',
  'unit',
  'city',
  'state',
  'postalCode',
] as const satisfies readonly (keyof ManualAddressFields)[];

export type ManualAddressDraft = {
  v: 1;
  searchSessionId?: string;
  query?: string;
  street: string;
  unit: string;
  city: string;
  state: string;
  postalCode: string;
  edited: boolean;
  expiresAt: number;
  id?: string;
  result?: string[];
};

export type ManualAddressPrefill = ManualAddressFields & {
  id?: string;
  result?: string[];
};

export type ManualAddressHandoff = {
  now: number;
  searchSessionId?: string;
  query?: string;
  fields?: Partial<ManualAddressFields>;
  id?: string;
  result?: string[];
  /** A second "Enter address manually" click must not parse over seller edits. */
  keepEdits?: boolean;
};

const EMPTY_FIELDS: ManualAddressFields = {
  street: '',
  unit: '',
  city: '',
  state: '',
  postalCode: '',
};

const US_STATES = new Set<string>(US_STATE_CODES);

// Same labels Beacon uses when it formats a unit onto a confirmation line.
const UNIT_LABEL =
  /^(?:#|apt\.?|apartment|suite|ste\.?|unit|fl\.?|floor|bldg\.?|building|rm\.?|room|dept\.?|lot|slip|trlr|trailer|spc\.?|space)\b/i;

const PO_BOX = /^(?:p\.?\s*o\.?\s*box|post\s+office\s+box)\b/i;
const TRAILING_STATE = /,\s*([A-Za-z]{2})(?:\s+(\d{5})(?:-\d{4})?)?\s*$/;
const INLINE_UNIT =
  /^(.*\d.*?)(?:\s+)((?:#|apt\.?|apartment|suite|ste\.?|unit|fl\.?|floor|bldg\.?|building|rm\.?|room)\s+\S.*)$/i;

const blankFields = (): ManualAddressFields => ({ ...EMPTY_FIELDS });

const sameText = (left: string, right: string): boolean =>
  left.trim().toLowerCase() === right.trim().toLowerCase();

const zip5 = (value: string): string => value.replace(/\D/g, '').slice(0, 5);

const unitKey = (value: string): string =>
  value.trim().replace(UNIT_LABEL, '').replace(/^#/, '').trim().toLowerCase();

const trimmedFields = (fields: Partial<ManualAddressFields> | undefined): ManualAddressFields => ({
  street: fields?.street?.trim() ?? '',
  unit: fields?.unit?.trim() ?? '',
  city: fields?.city?.trim() ?? '',
  state: fields?.state?.trim() ?? '',
  postalCode: fields?.postalCode?.trim() ?? '',
});

const hasAnyField = (fields: ManualAddressFields): boolean =>
  Object.values(fields).some((value) => value.trim() !== '');

export function parseManualAddressQuery(query: string): ManualAddressFields {
  const text = query.trim();
  if (!text) return blankFields();

  const poBox = PO_BOX.test(text);
  let state = '';
  let postalCode = '';
  let rest = text;
  const stateMatch = TRAILING_STATE.exec(text);
  const stateCode = stateMatch?.[1]?.toUpperCase();
  if (stateMatch && stateCode && US_STATES.has(stateCode)) {
    state = stateCode;
    postalCode = stateMatch[2] ?? '';
    rest = text.slice(0, stateMatch.index).trim();
  }

  if (!state) {
    const unitOnly = splitRest(rest, poBox, false);
    if (unitOnly.unit) return { ...blankFields(), street: unitOnly.street, unit: unitOnly.unit };
    return { ...blankFields(), street: text };
  }

  if (!rest) return { ...blankFields(), street: text, state, postalCode };
  const parsed = splitRest(rest, poBox, true);
  if (!parsed.street.trim()) return { ...blankFields(), street: text };
  return { street: parsed.street, unit: parsed.unit, city: parsed.city, state, postalCode };
}

function splitRest(
  rest: string,
  poBox: boolean,
  hasState: boolean,
): Pick<ManualAddressFields, 'street' | 'unit' | 'city'> {
  const parts = rest
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const streetPart = parts[0] ?? '';
  if (!streetPart) return { street: '', unit: '', city: '' };

  const unitParts: string[] = [];
  const other: string[] = [];
  for (const part of parts.slice(1)) {
    if (!poBox && UNIT_LABEL.test(part)) unitParts.push(part);
    else other.push(part);
  }

  let street = streetPart;
  let unit = unitParts.join(' ');
  let city = '';
  if (hasState && other.length > 0) {
    city = other[other.length - 1] ?? '';
    const extra = other.slice(0, -1);
    if (extra.length > 0) street = [street, ...extra].join(', ');
  } else if (other.length > 0) {
    street = [street, ...other].join(', ');
  }

  if (!poBox && !unit) {
    const inline = INLINE_UNIT.exec(street);
    const streetBeforeUnit = inline?.[1]?.trim();
    const inlineUnit = inline?.[2]?.trim();
    if (streetBeforeUnit && inlineUnit) {
      street = streetBeforeUnit;
      unit = inlineUnit;
    }
  }

  return { street, unit, city };
}

export function reviewSellerAddress(
  seller: ManualAddressFields,
  casa: ManualAddressFields & { id?: string },
): {
  merged: ManualAddressFields;
  conflicts: Partial<ManualAddressFields>;
  unitDropped: boolean;
} {
  const unitDropped = Boolean(seller.unit.trim() && casa.id?.trim() && !casa.unit.trim());
  const merged: ManualAddressFields = { ...seller };
  const conflicts: Partial<ManualAddressFields> = {};
  const same: Record<keyof ManualAddressFields, (left: string, right: string) => boolean> = {
    street: (left, right) => !streetsMateriallyDiffer(left, right),
    unit: (left, right) => unitKey(left) === unitKey(right),
    city: sameText,
    state: sameText,
    postalCode: (left, right) => zip5(left) === zip5(right),
  };

  MANUAL_ADDRESS_FIELD_KEYS.forEach((key) => {
    const sellerValue = seller[key].trim();
    const casaValue = casa[key].trim();
    if (!sellerValue && casaValue) {
      merged[key] = key === 'postalCode' ? zip5(casaValue) : casaValue;
      return;
    }
    if (key === 'unit' && unitDropped) return;
    if (sellerValue && casaValue && !same[key](seller[key], casa[key])) {
      conflicts[key] = key === 'postalCode' ? zip5(casaValue) : casaValue;
    }
  });

  return { merged, conflicts, unitDropped };
}

export function planManualAddressDraft(
  existing: ManualAddressDraft | null,
  input: ManualAddressHandoff,
): ManualAddressDraft | null {
  const fresh = existing !== null && existing.v === 1 && existing.expiresAt > input.now;
  const sameSession = fresh && (existing?.searchSessionId ?? '') === (input.searchSessionId ?? '');
  const incomingQuery = input.query?.trim() ?? '';
  const storedQuery = existing?.query?.trim() ?? '';
  // An edited draft survives Back only for the address the seller was editing.
  const sameQuery = incomingQuery === '' || incomingQuery === storedQuery;
  if (input.keepEdits && sameSession && existing?.edited && sameQuery) return null;

  const fields = trimmedFields(input.fields);
  const query = input.query?.trim();
  const result = input.result?.filter((code) => code.trim() !== '');
  return {
    v: 1,
    ...(input.searchSessionId ? { searchSessionId: input.searchSessionId } : {}),
    ...(query ? { query } : {}),
    ...fields,
    ...(input.id ? { id: input.id } : {}),
    ...(result && result.length > 0 ? { result } : {}),
    edited: false,
    expiresAt: input.now + MANUAL_ADDRESS_DRAFT_TTL_MS,
  };
}

export function prefillFields(
  draft: ManualAddressDraft,
  searchSessionId: string | undefined,
  now: number,
): ManualAddressPrefill | null {
  if (draft.v !== 1 || draft.expiresAt <= now) return null;
  if ((draft.searchSessionId ?? '') !== (searchSessionId ?? '')) return null;
  const fields = trimmedFields(draft);
  if (hasAnyField(fields)) {
    // A resolved building can omit the unit the seller typed. The query still
    // has it. Fill only blank fields, and only before the seller edits.
    if (!draft.edited && draft.query) {
      const fromQuery = parseManualAddressQuery(draft.query);
      for (const key of MANUAL_ADDRESS_FIELD_KEYS) {
        if (!fields[key] && fromQuery[key]) fields[key] = fromQuery[key];
      }
    }
    return fields;
  }
  if (draft.query) return parseManualAddressQuery(draft.query);
  return null;
}

const parseDraft = (raw: string | null): ManualAddressDraft | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const draft = parsed as Partial<ManualAddressDraft>;
    if (draft.v !== 1) return null;
    return {
      v: 1,
      ...(typeof draft.searchSessionId === 'string'
        ? { searchSessionId: draft.searchSessionId }
        : {}),
      ...(typeof draft.query === 'string' ? { query: draft.query } : {}),
      street: typeof draft.street === 'string' ? draft.street : '',
      unit: typeof draft.unit === 'string' ? draft.unit : '',
      city: typeof draft.city === 'string' ? draft.city : '',
      state: typeof draft.state === 'string' ? draft.state : '',
      postalCode: typeof draft.postalCode === 'string' ? draft.postalCode : '',
      edited: draft.edited === true,
      expiresAt: typeof draft.expiresAt === 'number' ? draft.expiresAt : 0,
      ...(typeof draft.id === 'string' && draft.id ? { id: draft.id } : {}),
      ...(Array.isArray(draft.result)
        ? {
            result: draft.result.filter(
              (code): code is string => typeof code === 'string' && code !== '',
            ),
          }
        : {}),
    };
  } catch {
    return null;
  }
};

const draftStorage = (): Storage | null => {
  try {
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
};

const readDraftRaw = (): string | null => {
  const storage = draftStorage();
  if (!storage) return null;
  try {
    return storage.getItem(MANUAL_ADDRESS_DRAFT_KEY);
  } catch {
    return null;
  }
};

const writeDraftRaw = (value: string): void => {
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.setItem(MANUAL_ADDRESS_DRAFT_KEY, value);
  } catch {
    // A blocked or full sessionStorage must not stop navigation or submit.
  }
};

export function readManualAddressPrefill(
  searchSessionId?: string,
  now = Date.now(),
): ManualAddressPrefill | null {
  const storage = draftStorage();
  if (!storage) return null;
  const draft = parseDraft(readDraftRaw());
  if (!draft) return null;
  if (draft.expiresAt <= now) {
    try {
      storage.removeItem(MANUAL_ADDRESS_DRAFT_KEY);
    } catch {
      // Leaving an expired draft is better than failing the page.
    }
    return null;
  }
  const fields = prefillFields(draft, searchSessionId, now);
  if (!fields) return null;
  return {
    ...fields,
    ...(draft.id ? { id: draft.id } : {}),
    ...(draft.result && draft.result.length > 0 ? { result: draft.result } : {}),
  };
}

export function rememberManualAddressHandoff(
  input: Omit<ManualAddressHandoff, 'now'> & { now?: number },
): void {
  const existing = parseDraft(readDraftRaw());
  const next = planManualAddressDraft(existing, { ...input, now: input.now ?? Date.now() });
  if (!next) return;
  writeDraftRaw(JSON.stringify(next));
}

export function markManualAddressDraftEdited(
  fields: ManualAddressFields,
  searchSessionId?: string,
  now = Date.now(),
  extras?: { id?: string; result?: string[] },
): void {
  const existing = parseDraft(readDraftRaw());
  const searchSession = searchSessionId ?? existing?.searchSessionId;
  const id = extras ? extras.id : existing?.id;
  const result = extras ? extras.result : existing?.result;
  const next: ManualAddressDraft = {
    v: 1,
    ...(searchSession ? { searchSessionId: searchSession } : {}),
    ...(existing?.query ? { query: existing.query } : {}),
    ...trimmedFields(fields),
    ...(id ? { id } : {}),
    ...(result && result.length > 0 ? { result } : {}),
    edited: true,
    expiresAt: now + MANUAL_ADDRESS_DRAFT_TTL_MS,
  };
  writeDraftRaw(JSON.stringify(next));
}

let cachedDraftRaw: string | null | undefined;
let cachedDraftSession: string | undefined;
let cachedDraft: ManualAddressPrefill | null = null;

export function subscribeManualAddressDraft(): () => void {
  return () => {};
}

/** Cached so useSyncExternalStore does not loop. The server snapshot stays empty. */
export function readManualAddressDraftSnapshot(
  searchSessionId?: string,
): ManualAddressPrefill | null {
  const raw = readDraftRaw();
  if (raw === cachedDraftRaw && searchSessionId === cachedDraftSession) return cachedDraft;
  cachedDraftRaw = raw;
  cachedDraftSession = searchSessionId;
  cachedDraft = readManualAddressPrefill(searchSessionId);
  return cachedDraft;
}

export function clearManualAddressDraft(): void {
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.removeItem(MANUAL_ADDRESS_DRAFT_KEY);
  } catch {
    // A failed clear must not turn a successful handoff into an error.
  }
}
